import { supabase } from "@/integrations/supabase/client";

interface AIConfig {
  temperature?: number;
  max_tokens?: number;
  top_p?: number;
  enableThinking?: boolean;
}

type ClaudeContentBlock =
  | { type: 'text'; text: string }
  | { type: 'thinking'; thinking?: string; text?: string }
  | { type: 'redacted_thinking'; data?: string }
  | {
      type: 'image';
      source:
        | { type: 'base64'; media_type: string; data: string }
        | { type: 'url'; url: string };
    };

interface ClaudeMessage {
  role: 'user' | 'assistant';
  content: string | ClaudeContentBlock[];
}

interface ClaudeCompletionResult {
  content: string;
  tokensUsed: number;
  thinkingText?: string;
  providerExposedThinking?: boolean;
}

function extractClaudeResponse(data: any) {
  if (!Array.isArray(data?.content)) {
    return {
      content: '',
      thinkingText: '',
      providerExposedThinking: false,
    };
  }

  const answerParts: string[] = [];
  const thinkingParts: string[] = [];

  data.content.forEach((block: any) => {
    if (block?.type === 'text' && typeof block?.text === 'string' && block.text.trim()) {
      answerParts.push(block.text.trim());
      return;
    }

    if (block?.type === 'thinking') {
      const blockThinking = typeof block?.thinking === 'string'
        ? block.thinking
        : typeof block?.text === 'string'
          ? block.text
          : '';

      if (blockThinking.trim()) {
        thinkingParts.push(blockThinking.trim());
      }
    }
  });

  return {
    content: answerParts.join('\n').trim(),
    thinkingText: thinkingParts.join('\n\n').trim(),
    providerExposedThinking: thinkingParts.length > 0,
  };
}

export async function generateClaudeCompletion(
  messages: ClaudeMessage[],
  model: string = 'claude-sonnet-4-6',
  system?: string,
  config?: AIConfig
): Promise<ClaudeCompletionResult> {
  let retries = 3;
  let lastError: unknown = null;
  let backoffDelay = 1000;

  while (retries > 0) {
    try {
      const { data, error } = await supabase.functions.invoke('claude-proxy', {
        body: {
          model,
          system,
          messages,
          max_tokens: config?.max_tokens ?? 1500,
          temperature: config?.temperature,
          top_p: config?.top_p,
          enableThinking: config?.enableThinking ?? false,
        },
      });

      if (error) {
        throw new Error(error.message || 'Failed to get response from Claude');
      }

      if (!data || data.error) {
        const errorMessage =
          typeof data?.error === 'string'
            ? data.error
            : typeof data?.error?.message === 'string'
              ? data.error.message
              : 'Failed to get response from Claude';
        throw new Error(errorMessage);
      }

      const parsed = extractClaudeResponse(data);
      const tokensUsed =
        Number(data?.tokensUsed) ||
        Number(data?.usage?.input_tokens || 0) + Number(data?.usage?.output_tokens || 0);

      return {
        content: parsed.content,
        tokensUsed,
        thinkingText: parsed.thinkingText || undefined,
        providerExposedThinking: parsed.providerExposedThinking,
      };
    } catch (error: any) {
      lastError = error;
      const message = String(error?.message || 'Unknown Claude error');
      const isRetryable =
        /temporarily unavailable|network|service|timeout|429|500|502|503|504|fetch/i.test(message) ||
        error?.name === 'AbortError';

      if (isRetryable && retries > 1) {
        retries -= 1;
        const jitter = Math.random() * 0.3 * backoffDelay;
        await new Promise((resolve) => setTimeout(resolve, backoffDelay + jitter));
        backoffDelay *= 2;
        continue;
      }

      throw error;
    }
  }

  throw lastError || new Error('Failed to get response from Claude after multiple attempts.');
}

export async function checkClaudeConnection(): Promise<boolean> {
  try {
    const { data, error } = await supabase.functions.invoke('claude-proxy', {
      body: { action: 'check_connection' },
    });

    if (error) return false;
    return Boolean(data?.success);
  } catch {
    return false;
  }
}
